// The caster's own Mana/Health/Charge -- the first real mechanic behind the HUD
// bars, which had been static/full since PR #7. Mana is what every cast draws
// from to pay its cost, and 04_Physics_Engine.md's Danger list already names
// "an Unbound draw forcing a shortfall out of Health" -- this just wires that up.
//
// As of 2026-07-30 (sim extraction step 2) the numbers themselves live in the shared
// WorldState (rune-lab/src/sim/state.ts) rather than in module-level `let`s here;
// this module is now the game's accessor layer over them, with exactly the same
// public API it had before, so no call site changed. Restored from a save via
// getPlayerStatsSnapshot/setPlayerStatsSnapshot (see gameSave.ts/ui.ts), reset on
// New Game.
// The rules themselves -- what a draw takes, when a shortfall costs Health, where
// a give clamps -- moved to rune-lab/src/sim/player.ts on 2026-07-30 (step 4b),
// because the cast handlers that apply them moved to the sim and cannot import
// from `game/`. Each function below is now the same name and signature bound to
// the game's single world, so no call site changed.
import { getWorld } from './activeWorld';
import * as Player from '../../rune-lab/src/sim/player';
import { SimEvents } from '../../rune-lab/src/sim/events';

// The ceilings the HUD bars are drawn against. Read off the world's own
// PhysicsConfig (2026-07-31, Phase 1) rather than imported as constants, so a
// world running on tuned numbers draws bars against the numbers it is actually
// running on -- the alternative is a HUD that quietly disagrees with the sim.
export function getMaxMana(): number {
  return getWorld().physics.player.maxMana;
}

export function getMaxHealth(): number {
  return getWorld().physics.player.maxHealth;
}

export function getMaxCharge(): number {
  return getWorld().physics.player.maxCharge;
}

export function getMaxThermalState(): number {
  return getWorld().physics.player.maxThermalState;
}

export function getThermalFrozenThreshold(): number {
  return getWorld().physics.player.thermalFrozenThreshold;
}

export function getThermalBurningThreshold(): number {
  return getWorld().physics.player.thermalBurningThreshold;
}

export function getMana(): number {
  return Player.getMana(getWorld());
}

export function getThermalState(): number {
  return Player.getThermalState(getWorld());
}

export function getHealth(): number {
  return Player.getHealth(getWorld());
}

export function getCharge(): number {
  return Player.getCharge(getWorld());
}

/** Draws up to `amount` Mana, taking only what's actually in reserve -- the safe
 * path (Precise/Standard draws): coming up short just weakens the effect, same as an
 * ordinary object source running low. Returns how much was actually drawn. */
export function drawMana(amount: number): number {
  return Player.drawMana(getWorld(), amount);
}

/** Forces the full requested amount out regardless of reserve: whatever Mana
 * doesn't cover comes out of Health instead. This is the "attempted an Unbound draw
 * the caster's body couldn't actually cover" danger case, not ordinary use --
 * see 10_Diary.md. Returns the Health cost (0 if Mana alone covered it).
 *
 * Unused today -- every real caller goes through casts.ts's applyTransferEffect/
 * applyMomentumEffect, which call the sim function directly and already have an
 * events array to hand it. Kept for the same reason every other function in this
 * file mirrors its sim counterpart's signature: so this module never quietly
 * drifts out of sync with the rules it delegates to (see this file's own header
 * comment) if something new ever does call it from here instead. */
export function forceDrawMana(amount: number, out: SimEvents): number {
  return Player.forceDrawMana(getWorld(), amount, out);
}

export function giveMana(amount: number): void {
  Player.giveMana(getWorld(), amount);
}

/** Draws up to `amount` Charge, taking only what's actually in reserve.
 * Deliberately has no `forceDrawCharge` counterpart: unlike Energy, there is no
 * ambient bioelectric reserve to force the shortfall out of Health from, so a
 * Charge draw -- even an over-ambitious Unbound one -- always just weakens if the
 * player hasn't charged up enough first, never costs Health. Returns how much
 * was actually drawn. */
export function drawCharge(amount: number): number {
  return Player.drawCharge(getWorld(), amount);
}

export function giveCharge(amount: number): void {
  Player.giveCharge(getWorld(), amount);
}

export function resetPlayerStats(): void {
  const world = getWorld();
  world.player.mana = world.physics.player.maxMana;
  world.player.health = world.physics.player.maxHealth;
  world.player.charge = 0;
  world.player.thermalState = world.physics.player.thermalStart;
}

export interface PlayerStatsSnapshot {
  mana: number;
  health: number;
  charge: number;
  thermalState: number;
}

export function getPlayerStatsSnapshot(): PlayerStatsSnapshot {
  const { mana, health, charge, thermalState } = getWorld().player;
  return { mana, health, charge, thermalState };
}

/** `snapshot.energy` is what a save written before the Mana/thermalState split
 * (2026-08-05) stored under -- that field's own behavior (cast cost + passive
 * regen) is exactly what `mana` now is, so an old save's `energy` maps onto
 * the new `mana` field. `thermalState` didn't exist at all before the split,
 * so a save that predates it defaults to the same neutral thermalStart a
 * fresh game starts at. */
export function setPlayerStatsSnapshot(snapshot: PlayerStatsSnapshot & { energy?: number }): void {
  const world = getWorld();
  const player = world.player;
  player.mana = snapshot.mana ?? snapshot.energy ?? world.physics.player.maxMana;
  player.health = snapshot.health;
  // Saves from before Charge existed won't have this field.
  player.charge = snapshot.charge ?? 0;
  player.thermalState = snapshot.thermalState ?? world.physics.player.thermalStart;
}
